'use client';

import { Button } from '@/components/ui/button';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import useRegisterStore from '@/stores/register';
import { useState, useActionState, useEffect, startTransition, useRef } from 'react';
import { ArrowLeft } from 'lucide-react';
import { verifyPreRegistrationWhatsApp, type VerifyPreRegistrationWhatsAppActionState } from '@/app/(auth)/actions';
import { toast } from '@/components/toast';
import { Spinner } from '@/components/ui/spinner';
import useRudderStackAnalytics from '@/hooks/useRudderAnalytics';
import useIsWebview from '@/hooks/use-is-webview';
const OtpVerificationStep = () => {
  const {
    previousStep,
    nextStep,
    formData,
    resendWhatsAppCode,
    sendSmsCode,
    isSendingWhatsAppCode,
    resendCooldownRemaining,
    resendCooldownEnd,
    tickResendCooldown,
    setVerificationTokenId
  } = useRegisterStore(['previousStep', 'nextStep', 'formData', 'resendWhatsAppCode', 'sendSmsCode', 'isSendingWhatsAppCode', 'resendCooldownRemaining', 'resendCooldownEnd', 'tickResendCooldown', 'setVerificationTokenId']);
  const analytics = useRudderStackAnalytics();
  const [isWebView] = useIsWebview();
  const [otp, setOtp] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [state, verifyAction] = useActionState<VerifyPreRegistrationWhatsAppActionState, FormData>(verifyPreRegistrationWhatsApp, {
    status: 'idle'
  });
  const hasTrackedPageView = useRef(false);
  const phoneNumber = formData.phoneNumber;
  const formattedPhone = phoneNumber ? `55${phoneNumber.replace(/\D/g, '').trim()}` : '';
  const handleNext = () => {
    if (otp.length === 6 && !isVerifying) {
      setIsVerifying(true);
      const formData = new FormData();
      formData.append('code', otp);
      formData.append('phoneNumber', formattedPhone);
      startTransition(() => {
        verifyAction(formData);
      });
    }
  };
  const handleResend = async () => {
    if (!isSendingWhatsAppCode && resendCooldownRemaining === 0) {
      setOtp('');
      await resendWhatsAppCode();
    }
  };
  const handleSendSms = async () => {
    if (!isSendingWhatsAppCode && resendCooldownRemaining === 0) {
      setOtp('');
      await sendSmsCode();
    }
  };

  // Countdown timer for resend cooldown. Key off the end timestamp so the
  // interval is started once and torn down when the cooldown is cleared.
  useEffect(() => {
    if (!resendCooldownEnd) return;
    const interval = setInterval(tickResendCooldown, 1000);
    return () => clearInterval(interval);
  }, [resendCooldownEnd, tickResendCooldown]);

  // Auto-verify when 6 digits are entered
  useEffect(() => {
    if (otp.length === 6 && !isVerifying) {
      handleNext();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp]);

  // Handle verification state changes
  useEffect(() => {
    if (state.status === 'success') {
      // Persist the OTP proof id so the final register() submit can pass it
      // back to the action for atomic consumption (issue #5339).
      if (state.verificationTokenId) {
        setVerificationTokenId(state.verificationTokenId);
      }
      toast({
        type: 'success',
        description: 'Código verificado com sucesso!'
      });
      setIsVerifying(false);
      nextStep();
    }
    if (state.status === 'invalid_code') {
      toast({
        type: 'error',
        description: 'Código inválido. Tente novamente.'
      });
      setOtp('');
      setIsVerifying(false);
    }
    if (state.status === 'expired_code') {
      toast({
        type: 'error',
        description: 'Código expirado. Solicite um novo código.'
      });
      setOtp('');
      setIsVerifying(false);
    }
    if (state.status === 'failed') {
      toast({
        type: 'error',
        description: 'Erro ao verificar código. Tente novamente.'
      });
      setIsVerifying(false);
    }
    if (state.status === 'invalid_data') {
      toast({
        type: 'error',
        description: 'Dados inválidos. Verifique o código e tente novamente.'
      });
      setIsVerifying(false);
    }
  }, [state.status, state.verificationTokenId, nextStep, setVerificationTokenId]);
  useEffect(() => {
    if (!analytics || hasTrackedPageView.current) return;
    hasTrackedPageView.current = true;
    analytics.track('Register Step', {
      step: 'otp_verification',
      platform: isWebView ? 'app' : 'web'
    });
  }, [analytics, isWebView]);
  return <div className="min-h-screen w-full flex flex-col md:max-w-md md:mx-auto px-4 py-8 md:py-20">
      <div className="flex-1 flex flex-col justify-between md:justify-start gap-4 md:gap-10 w-full">
        <div className="flex flex-col gap-4 md:gap-10 items-start">
          <Button type="button" variant="none" size="iconArea" onClick={previousStep} className="text-primary  md:hidden hover:text-primary/80 transition-colors">
            <ArrowLeft className="size-5" />
          </Button>
          <div className="flex flex-col gap-1 w-full md:items-center">
            <h1 className="text-primary text-2xl font-bold">Verificação</h1>
            <p className="text-muted-foreground text-sm max-w-[300px] md:max-w-full md:text-center">
              Enviamos um código de 6 dígitos para o WhatsApp do número{' '}
              {phoneNumber && `${phoneNumber}`}
            </p>
          </div>

          <div className="flex flex-col w-full md:max-w-xs mx-auto gap-4">
            <div className="w-full">
              <InputOTP maxLength={6} value={otp} onChange={value => setOtp(value)} disabled={isVerifying}>
                <InputOTPGroup className="w-full">
                  <InputOTPSlot index={0} className="w-full h-14 bg-transparent border  border-black/20 dark:border-white/20 rounded-l-xl text-primary text-2xl" />
                  <InputOTPSlot index={1} className="w-full h-14 bg-transparent border border-l-0 border-black/20 dark:border-white/20 text-primary text-2xl" />
                  <InputOTPSlot index={2} className="w-full h-14 bg-transparent border border-l-0 border-black/20 dark:border-white/20 text-primary text-2xl" />
                  <InputOTPSlot index={3} className="w-full h-14 bg-transparent border border-l-0 border-black/20 dark:border-white/20 text-primary text-2xl" />
                  <InputOTPSlot index={4} className="w-full h-14 bg-transparent border border-x-0 border-black/20 dark:border-white/20 text-primary text-2xl" />
                  <InputOTPSlot index={5} className="w-full h-14 bg-transparent border   border-black/20 dark:border-white/20 rounded-r-xl text-primary text-2xl" />
                </InputOTPGroup>
              </InputOTP>
            </div>

            <div className="text-center w-full">
              {isVerifying ? <div className="flex items-center justify-center gap-2 py-2">
                  <Spinner size="lg" spinnerClassName="bg-primary" />
                  <span className="text-primary text-sm">Verificando...</span>
                </div> : <>
                  <p className="text-primary/70 text-sm">
                    Não recebeu o código?{' '}
                    <button type="button" onClick={handleResend} disabled={isSendingWhatsAppCode || resendCooldownRemaining > 0} className="text-primary underline hover:text-primary/80 transition-colors disabled:text-muted-foreground disabled:no-underline disabled:cursor-not-allowed">
                      {isSendingWhatsAppCode ? 'Reenviando...' : resendCooldownRemaining > 0 ? `Reenviar em ${resendCooldownRemaining}s` : 'Reenviar'}
                    </button>
                  </p>
                  <p className="text-primary/70 text-sm">
                    Problemas para receber pelo WhatsApp?{' '}
                    <button type="button" onClick={handleSendSms} disabled={isSendingWhatsAppCode || resendCooldownRemaining > 0} className="text-primary underline hover:text-primary/80 transition-colors disabled:text-muted-foreground disabled:no-underline disabled:cursor-not-allowed">
                      {resendCooldownRemaining > 0 ? `Enviar por SMS em ${resendCooldownRemaining}s` : 'Enviar por SMS'}
                    </button>
                  </p>
                </>}
            </div>
          </div>
        </div>

        {/* <Button
          type="button"
          onClick={handleNext}
          disabled={otp.length !== 6 || isVerifying}
          size="xl"
          rounded="full"
          className="md:max-w-[240px] md:mx-auto  font-semibold w-full"
         >
          {isVerifying ? (
            <div className="flex items-center gap-2">
              <Spinner size="lg" spinnerClassName="bg-white dark:bg-black" />
              <span>Verificando...</span>
            </div>
          ) : (
            "Verificar"
          )}
         </Button> */}
      </div>
    </div>;
};
export default OtpVerificationStep;